/**
 * A markdown string, rendered the way a document is rendered.
 *
 * Not a second markdown pipeline: `markdownToDocument` is §5.1's parser and
 * `editorExtensions` is §5.1's component set, so a help page and a published
 * handbook go through exactly the same code and land in the same
 * `.mx-document-body` stylesheet. A help page that rendered its own way would
 * be the one place in the app where a table or a callout looked different, and
 * it would drift the first time either side changed.
 *
 * Tiptap rather than a static HTML string because Tiptap is what is already
 * installed — `@tiptap/html`'s `generateHTML` is a separate package, and a
 * non-editable editor costs a ProseMirror instance on a page nobody types into.
 * That is a real cost and a small one; a second renderer is neither.
 */

import { markdownToDocument } from '@matchbox/shared/markdown';
import type { JSONContent } from '@tiptap/core';
import { EditorContent, useEditor } from '@tiptap/react';
import { useMemo } from 'react';
import { EditorStyles } from '../../editor/editor-styles.tsx';
import { editorExtensions } from '../../editor/extensions.ts';

export function Prose({ markdown }: { markdown: string }) {
  const content = useMemo(() => markdownToDocument(markdown) as JSONContent, [markdown]);

  const editor = useEditor(
    {
      editable: false,
      extensions: editorExtensions(),
      content,
      editorProps: { attributes: { class: 'mx-document-body' } },
    },
    [content],
  );

  return (
    <>
      <EditorStyles />
      <EditorContent editor={editor} className="mx-document" />
    </>
  );
}
